// What the month screen shows, worked out from the month payload. Pure: no
// React, no clock, no I/O, so every rule below is unit-testable on its own -
// the same split `day/day-model.ts` and `shell/screens.ts` keep.
//
// Nothing here decides what is DUE. That is the server's answer, computed from
// the same rules the day screen's is (AGENTS.md: a client must not be able to
// disagree with the API about what falls on a day). What is here is how thirty
// or thirty-one answers become a grid, what each square says to a screen reader,
// and the one sentence the month says back to you.

import type { DayChoice } from '../day/DayScreen.tsx';
import type { IsoDay, IsoMonth, MonthDay, MonthResponse, MonthSection } from '../api/client.ts';
import { addDays, dayAndMonth, monthName, weekdayIndex, weekdayName } from '../iso-day.ts';

/**
 * The column headers, Sunday first - the week the prototype's own day pickers
 * are drawn on (`.dows` in prototype.html).
 *
 * Initials repeat: two S's and two T's. They are drawn `aria-hidden` for that
 * reason, and every square carries its own weekday in its accessible name
 * instead, so nothing is left to a header a screen reader cannot tell apart.
 */
export const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

/**
 * How many schedules a square draws before it starts counting them instead.
 *
 * An account may hold ten (AGENTS.md), and ten dots inside a square that is
 * 44px across on a phone is a smudge rather than information. Past this the
 * last dot becomes a figure, which is legible at any width.
 */
export const MAX_DOTS = 4;

function things(n: number): string {
  return `${n} thing${n === 1 ? '' : 's'}`;
}

/**
 * The squares of the grid: the blanks before the 1st, then every day.
 *
 * Leading blanks only. A CSS grid needs nothing after the last day - the row
 * simply ends - and trailing nulls would be squares to remember not to draw.
 */
export function gridCells(view: MonthResponse): (MonthDay | null)[] {
  // The payload is checked on arrival (`useMonthScreen`), so `firstDay` is a
  // day here; the fallback exists because the type system cannot prove it.
  const lead = weekdayIndex(view.firstDay) ?? 0;
  return [...(Array<null>(lead).fill(null) as null[]), ...view.days];
}

/**
 * The month a day falls in.
 *
 * String work on a day the SERVER named, and never a clock: the client is told
 * which day today is and slices it, exactly as it slices a weekday out of one to
 * lay the grid out. Which month TODAY is stays the server's answer entirely -
 * that is the time-zone question, and it is asked as the literal `'this'`
 * whenever there is no named day to slice. This is what turns a day already on
 * screen into the month around it: the control in the date header (`App.tsx`),
 * and comparing the month on screen with the one today is in.
 */
export function monthOfDay(day: IsoDay): IsoMonth {
  return day.slice(0, 7);
}

/** The schedules due on one day, in the account's order, resolved from the legend. */
export function sectionsOn(cell: MonthDay, view: MonthResponse): MonthSection[] {
  return cell.sectionIds.flatMap((id) => {
    const section = view.sections.find((s) => s.id === id);
    return section === undefined ? [] : [section];
  });
}

/**
 * The dots one square draws, and how many schedules they stand for beyond them.
 *
 * `more` is 0 when every schedule got a dot, which is the ordinary case: the
 * figure only appears once a day has more schedules in it than a square can
 * hold legibly.
 */
export function dotsOn(
  cell: MonthDay,
  view: MonthResponse,
): { shown: MonthSection[]; more: number } {
  const all = sectionsOn(cell, view);
  if (all.length <= MAX_DOTS) return { shown: all, more: 0 };
  return { shown: all.slice(0, MAX_DOTS - 1), more: all.length - (MAX_DOTS - 1) };
}

/** Whether the day is after the month payload's own today. */
export function isFuture(cell: MonthDay, view: MonthResponse): boolean {
  return cell.day > view.today;
}

/**
 * What one square says to a screen reader.
 *
 * The whole square, in one string, because a grid of numbers with coloured dots
 * beside them is a picture: read out as its parts it is thirty-one bare numbers.
 * A future day with nothing done is SCHEDULED rather than "0 of 4 done" - the
 * same distinction the day screen makes when it draws no bar on a day nothing
 * was due on yet.
 *
 * Once something IS done on it, it says so. Ticking tomorrow off is allowed -
 * the day screen reaches it, and the server accepts a completion against a
 * future occurrence - and the month's own count moves when it happens, so a
 * square still reading "2 things scheduled" would contradict the count directly
 * above it. The square still gets NO BAR: that is the distinction between a day
 * nobody has reached and one that is behind, and it is `isFuture`'s to make.
 */
export function squareLabel(cell: MonthDay, view: MonthResponse): string {
  const when = `${weekdayName(cell.day)} ${dayAndMonth(cell.day)}`;
  const today = cell.day === view.today ? ', today' : '';
  if (cell.total === 0) return `${when}${today}, nothing due`;
  if (isFuture(cell, view) && cell.done === 0) {
    return `${when}${today}, ${things(cell.total)} scheduled`;
  }
  return `${when}${today}, ${cell.done} of ${cell.total} done`;
}

export interface MonthSummary {
  /**
   * The count the month heads itself with, or null when there is nothing to
   * count yet: a month entirely ahead of today is a PLAN, and "0 done" against
   * a month nobody has reached reads as failure rather than as ahead. The day
   * screen drops its bar on a future day for exactly this reason.
   */
  done: number | null;
  /** The one line under it. */
  line: string;
}

/**
 * The count and the sentence at the top of the month.
 *
 * A COUNT AND NOT A RATIO. `progress.total` includes days that have not happened
 * yet, so a bar or a percentage would draw a month two thirds ahead of itself as
 * two thirds failed - and a figure that only ever grows is what this product
 * shows instead of a streak (AGENTS.md, "Counts, never streaks").
 */
export function monthSummary(view: MonthResponse): MonthSummary {
  const schedules = view.sections.length;
  const line = `${things(view.progress.total)} scheduled across ${schedules} schedule${
    schedules === 1 ? '' : 's'
  }.`;
  return { done: view.firstDay > view.today ? null : view.progress.done, line };
}

/**
 * A month with nothing in it at all.
 *
 * Said plainly, and without pointing at a control: the + is the day screen's on
 * a phone and the sidebar's on a desktop, so naming one of them here would name
 * a button that is not on screen at the other width - which is the dead end this
 * client has been caught by five times (web/src/README.md).
 */
export function emptyMonthBody(view: MonthResponse): string {
  return (
    `Nothing in your schedules falls in ${monthName(view.firstDay)}. Perfect Note works out ` +
    'what is due from your repeats rather than storing a copy of every day, so a quiet month ' +
    'is genuinely quiet.'
  );
}

/**
 * Which of the day screen's three days this one is, or null when it is none.
 *
 * The month is a calendar of every day and the day screen reaches exactly three
 * (`DayScreen.tsx`: "Three days is what a daily planner is looked at through"),
 * so an "open this on Today" control can only honestly be offered for those
 * three. Null is what keeps it off the other twenty-eight rather than sending
 * somebody to a screen that would show them a different day.
 */
export function choiceFor(day: IsoDay, today: IsoDay): DayChoice | null {
  if (day === today) return 'today';
  if (day === addDays(today, -1)) return 'yesterday';
  if (day === addDays(today, 1)) return 'tomorrow';
  return null;
}
